import React, { memo, ReactElement, useEffect, useMemo, useRef, useCallback } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch-dom';
import { useRouter } from 'next/router';
import qs from 'qs';
import { useSearchState } from '../../search/hooks/useSearchState';
import { useAlgoliaAnalytics } from '~/shared/hooks';
import useScrollRestoration from '~/shared/hooks/useScrollRestoration';
import { useCookieConsent } from '~/shared/hooks/useCookieConsent';
import { useAlgoliaConfig } from '~/shared/hooks/useAlgoliaConfig';
import { useSession } from 'next-auth/react';
import { getEscapedKey } from '~/features/productsList/utils';
import { useAlgoliaConnector } from '~/shared/utils';

type ConnectorProps = {
    hitsPerPage?: number;
    children?: ReactElement | ReactElement[] | null;
    algoliaApp: string;
    algoliaToken: string;
    isSearch?: boolean;
    category?: string;
    context?: string;
    shouldControlUrl?: boolean;
    initialState?: unknown;
};

const createURL = (state: React.SetStateAction<qs.ParsedQs>) => {
    const newState = { ...state };
    /* Page not present in url sync
     *
     * https://github.com/algolia/react-instantsearch/issues/85
     * https://github.com/algolia/instantsearch.js/issues/2835
     *
     * The only solution in algolia for loading previous pages is presenting a load previous button
     * if page is synced only the default 24 items from the page passed are loaded, never the previous pages
     * */
    delete newState.hitsPerPage;

    const queryUrl = qs.stringify(newState, { skipNulls: true });
    return `?${decodeURIComponent(queryUrl)}`;
};

const searchStateToUrl = (searchState: React.SetStateAction<qs.ParsedQs>) =>
    searchState ? createURL(searchState).slice(1) : '';

const urlToSearchState = (search: string) => qs.parse(search.slice(1));

/* Algolia Instant Search */
export const AlgoliaClientSearch = memo(
    ({
        hitsPerPage = 24,
        children,
        algoliaApp,
        algoliaToken,
        category: unescapedCategory,
        context,
        shouldControlUrl = true,
        initialState,
    }: ConnectorProps) => {
        const router = useRouter();
        useScrollRestoration(router);
        const { initialize, initializeUserToken, initialized } = useAlgoliaAnalytics();
        const { data: session } = useSession();
        const query = useSearchState((state) => state.query);
        const isMounted = useRef(false);
        const { statistic } = useCookieConsent();
        const { defaultIndex } = useAlgoliaConfig();
        const algoliaClient = useAlgoliaConnector();

        const userToken = session?.user.customerNumber;
        const indexOfUrlQuery = router.asPath.indexOf('?');
        const category = getEscapedKey(unescapedCategory);

        const searchState = useMemo(() => {
            let location = {} as qs.ParsedQs;
            if (indexOfUrlQuery > -1) {
                const urlQuery = router.asPath.slice(indexOfUrlQuery);
                location = urlToSearchState(urlQuery);
            }
            return location;
        }, [indexOfUrlQuery, router.asPath]);

        const initializeAnalytics = useCallback(() => {
            initialize(algoliaApp, algoliaToken, defaultIndex);
        }, [algoliaApp, algoliaToken, defaultIndex, initialize]);

        const onSearchStateChange = useCallback(
            (updatedSearchState: qs.ParsedQs) => {
                if (searchState.query && searchState.query !== updatedSearchState.query) {
                    updatedSearchState.page = '1';
                }

                if (!shouldControlUrl) {
                    return;
                }

                const { pathname } = window.location;
                const oldState = searchStateToUrl(searchState);
                const newState = searchStateToUrl(updatedSearchState);

                if (newState && oldState !== newState) {
                    const newUrl = `${pathname}?${searchStateToUrl(updatedSearchState)}`;
                    router.replace(newUrl, undefined, { shallow: true });
                }
            },
            [router, searchState, shouldControlUrl]
        );

        useEffect(() => {
            if (statistic) {
                initializeAnalytics();
            }
        }, [initializeAnalytics, statistic]);

        useEffect(() => {
            if (statistic && userToken && initialized) {
                initializeUserToken(userToken);
            }
        }, [initializeUserToken, statistic, userToken, initialized]);

        useEffect(() => {
            if (isMounted.current && query !== undefined) {
                onSearchStateChange({
                    ...searchState,
                    query,
                });
            } else {
                isMounted.current = true;
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [query]);

        return (
            <InstantSearch
                indexName={defaultIndex}
                searchClient={{ ...algoliaClient, search: algoliaClient.search }}
                searchState={searchState}
                resultsState={initialState} // injects objects in first render. Created using findResultsState in SSR.
                onSearchStateChange={onSearchStateChange}
                createURL={shouldControlUrl ? createURL : undefined}
                refresh={false}
            >
                <Configure
                    hitsPerPage={hitsPerPage}
                    clickAnalytics={true}
                    ruleContexts={context ? [context] : undefined}
                    filters={category && !context ? `categoryKeys:'${category}'` : undefined}
                />
                {children}
            </InstantSearch>
        );
    }
);
